import { Box, Progress, Text } from "@mantine/core"
import { ChartLineUpIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import type { ICartPreview, ICartPreviewGift, ICartThresholdProgress } from "../../../apis/cart"
import type { CartItem } from "../../../store/Cart"
import { currencyFormat } from "../../../utils/currency"
import { DotPill } from "../../delivery/atoms"
import classes from "./CartDrawer.module.css"
import { ProductThumb } from "./ProductThumb"
import { cleanName } from "./utils"

// Non-price promotions in the cart (D3, canvas OrdB_PromoGift / OrdB_PromoTier).
// The order service applies them on create; everything here is display only.

/** Cart line name for a gift variant, else the promotion's name. */
const giftName = (gift: ICartPreviewGift, items: CartItem[]): { name: string; image?: string; skuCode?: string } => {
  const item = items.find((i) => i.id === gift.variantId)
  if (item) return { name: cleanName(item.nameMon) || item.skuCode || "-", image: item.image, skuCode: item.skuCode }
  return { name: gift.promotionName || "-" }
}

function ThresholdRow({ progress }: Readonly<{ progress: ICartThresholdProgress }>) {
  const { t } = useTranslation("order")
  const byQty = progress.unit === "QTY"
  const current = Number(progress.current) || 0
  const target = Number(progress.target) || 0
  if (!(target > 0)) return null
  const reached = current >= target
  const fmt = (n: number) => (byQty ? String(Math.round(n * 100) / 100) : currencyFormat(n))
  const chip = t(byQty ? "cart.thresholdChipQty" : "cart.thresholdChipAmount", { current: fmt(current), target: fmt(target) })
  const sentence = reached
    ? t("cart.thresholdReached", { discount: progress.discountText })
    : t(byQty ? "cart.thresholdNeedQty" : "cart.thresholdNeedAmount", {
        current: fmt(current),
        rest: fmt(target - current),
        discount: progress.discountText,
      })
  return (
    <Box className={classes.thresholdBox}>
      <Box className={classes.thresholdHead}>
        <Text className={classes.infoName}>
          <ChartLineUpIcon size={16} aria-hidden />
          {t(byQty ? "cart.thresholdTitleQty" : "cart.thresholdTitleAmount", { target: fmt(target), discount: progress.discountText })}
        </Text>
        <DotPill tone={reached ? "green" : "grey"}>{chip}</DotPill>
      </Box>
      <Progress size="sm" color={reached ? "green" : "dark"} value={Math.min(100, (current / target) * 100)} aria-label={chip} />
      <Text className={classes.sub} mt={6}>
        {sentence}
      </Text>
    </Box>
  )
}

/** Progress toward each threshold promotion of a supplier group (reached ones included). */
export function ThresholdProgressList({ preview }: Readonly<{ preview?: ICartPreview }>) {
  const rows = preview?.thresholdProgress || []
  if (rows.length === 0) return null
  return (
    <Box className={classes.info}>
      {rows.map((p) => (
        <ThresholdRow key={p.promotionId} progress={p} />
      ))}
    </Box>
  )
}

/** Gift rows the order service adds (0₮, tagged «Бэлэг»). */
export function GiftLines({ preview, items }: Readonly<{ preview?: ICartPreview; items: CartItem[] }>) {
  const { t } = useTranslation("order")
  const gifts = preview?.giftLines || []
  return (
    <>
      {gifts.map((gift) => {
        const view = giftName(gift, items)
        return (
          <Box key={`${gift.promotionId}:${gift.variantId}`} className={`${classes.line} ${classes.giftLine}`}>
            <Box className={classes.thumb}>
              <ProductThumb src={view.image} alt={view.name} iconSize={20} />
            </Box>
            <Box miw={0}>
              <Box className={classes.giftHead}>
                <Text className={classes.lineName}>{view.name}</Text>
                <DotPill tone="green">{t("cart.giftTag")}</DotPill>
              </Box>
              <Text className={classes.sub}>{t("cart.giftNote", { name: gift.promotionName })}</Text>
            </Box>
            <Box className={classes.lineSide}>
              <Text className={`${classes.sub} ${classes.mono}`}>{t("cart.giftQty", { count: gift.quantity })}</Text>
              <Text className={classes.amount}>{currencyFormat(0)}</Text>
            </Box>
          </Box>
        )
      })}
    </>
  )
}

/**
 * «Төлбөртэй мөр / Бэлгийн мөр / Багц, босгын хөнгөлөлт / Төлөх дүн» under a
 * supplier group; shown only when the group gets a gift or a discount.
 */
export function GroupPromoSummary({ amount, preview, discount }: Readonly<{ amount: number; preview?: ICartPreview; discount: number }>) {
  const { t } = useTranslation("order")
  const giftPieces = (preview?.giftLines || []).reduce((sum, g) => sum + (Number(g.quantity) || 0), 0)
  if (giftPieces === 0 && !(discount > 0)) return null
  return (
    <Box className={classes.groupSummary}>
      <Box className={classes.totalRow}>
        <Text span inherit>{t("cart.paidLines")}</Text>
        <Text span className={classes.amount}>{currencyFormat(amount)}</Text>
      </Box>
      {giftPieces > 0 && (
        <Box className={classes.totalRow}>
          <Text span inherit>{t("cart.giftLines", { count: giftPieces })}</Text>
          <Text span className={classes.amount}>{currencyFormat(0)}</Text>
        </Box>
      )}
      {discount > 0 && (
        <Box className={classes.totalRow}>
          <Text span inherit>{t("cart.promoDiscount")}</Text>
          <Text span className={classes.amount}>{`−${currencyFormat(discount)}`}</Text>
        </Box>
      )}
      <Box className={classes.totalRow}>
        <Text span fw={650} c="var(--nw-text)">{t("cart.groupPayable")}</Text>
        <Text span className={classes.amount}>{currencyFormat(Math.max(0, amount - discount))}</Text>
      </Box>
    </Box>
  )
}
